import React, { useEffect, useState } from "react";
import { serverErrorText } from "../lib/serverErrorText";
import { useTranslation } from "react-i18next";
import { authClient } from "../lib/authApi";
import { fmtMoney } from "../lib/format";
import { WarningOctagon } from "@phosphor-icons/react";
import { ReasonPicker, computeValidity } from "./ReasonPicker";
import { VIES_PRESETS } from "./reasonPresets";
import { labelFor } from "../lib/enumLabels";
import { useWorkflowOptional } from "../workflow/WorkflowContext";

const VIES_MIN_REASON_LEN = 8;

/**
 * v0.4.14.4 F4 — VIES-held review queue with override-with-reason.
 * Open to every signed-in user since v0.6.10 (api/routes_vies_override);
 * the section still hides if the endpoint ever answers 403.
 */
export const ViesHeldQueue = ({ calcId, locked = false, lockedBy = "signed" }) => {
  const { t } = useTranslation();
  const wf = useWorkflowOptional();
  const [rows, setRows] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [draft, setDraft] = useState({});
  // A2-554 (operator 2026-10-27): 167 rows each with its own picker was not
  // workable — folded by default, with one decision for the selected rows.
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [bulkReason, setBulkReason] = useState("");
  const [progress, setProgress] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await authClient.get(`/runs/calc/${calcId}/vies-held`);
      setRows(data || []);
    } catch (e) {
      if (e?.response?.status === 403) setHidden(true);
    }
  };
  useEffect(() => { if (calcId) refresh(); }, [calcId]);
  // A2-581 lead 10: an override acknowledges the row's review flags on the
  // server, so the review cards and Step 4's Continue must reload too (they
  // stayed as they were until some other refresh: "it did nothing").
  const afterDecision = () => {
    refresh();
    // only while this calc is still the one open: a long bulk run may end
    // after another calc was opened, and the shared review is that calc's
    if (wf?.calc?.id === calcId) wf?.refreshReview?.(calcId);
    window.dispatchEvent(new CustomEvent("review-updated", { detail: { calcId } }));
  };

  if (hidden || !calcId || rows.length === 0) return null;

  const pending = rows.filter((r) => !r.override);
  const netBy = rows.reduce((m, r) => {
    const c = r.currency || "";
    m[c] = (m[c] || 0) + (Number(r.net_amount) || 0);
    return m;
  }, {});
  const reasons = Object.entries(rows.reduce((m, r) => {
    for (const f of r.review_flags || []) m[f] = (m[f] || 0) + 1;
    return m;
  }, {})).sort((a, b) => b[1] - a[1]).slice(0, 3);
  const chosen = pending.filter((r) => picked.has(r.txn_id));
  const targetsOf = (r) => r.targets || ["B2B_ICS", "LOCAL_SALE", "DISTANCE_SALE"];
  const shared = chosen.length
    ? targetsOf(chosen[0]).filter((b) => chosen.every((r) => targetsOf(r).includes(b)))
    : [];
  const toggle = (id) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const applyBulk = async (decision) => {
    const reason = bulkReason.trim();
    const v = computeValidity(reason, VIES_MIN_REASON_LEN);
    if (!v.lengthOk || !v.placeholdersOk) {
      alert(t("ui.vies_held.reason_too_short"));
      return;
    }
    let done = 0;
    setProgress({ done, total: chosen.length });
    try {
      for (const r of chosen) {
        await authClient.post(`/runs/calc/${calcId}/vies-override`,
          { txn_id: r.txn_id, decision, reason });
        done += 1;
        setProgress({ done, total: chosen.length });
      }
    } catch (e) {
      alert(serverErrorText(e?.response?.data?.detail, "Override failed"));
    }
    setProgress(null);
    setPicked(new Set());
    setBulkReason("");
    if (done > 0) afterDecision(); else refresh();
  };

  const apply = async (txnId, overriddenTo) => {
    const reason = (draft[txnId]?.reason || "").trim();
    const v = computeValidity(reason, VIES_MIN_REASON_LEN);
    if (!v.lengthOk || !v.placeholdersOk) {
      alert(t("ui.vies_held.reason_too_short"));
      return;
    }
    try {
      // v0.4.16 R2 — backend renamed `overridden_to` → `decision`.
      await authClient.post(`/runs/calc/${calcId}/vies-override`,
        { txn_id: txnId, decision: overriddenTo, reason });
      setDraft((d) => ({ ...d, [txnId]: { ...d[txnId], reason: "" }}));
      afterDecision();
    } catch (e) {
      alert(serverErrorText(e?.response?.data?.detail, "Override failed"));
    }
  };

  return (
    <section data-testid="vies-held-section" className="border border-amber-700/40 bg-amber-50 dark:bg-amber-900/40">
      <header className="px-4 py-3 flex flex-wrap items-center gap-2">
        <WarningOctagon size={16} className="text-amber-600 dark:text-amber-400" />
        <span className="text-xs text-amber-700 dark:text-amber-300">
          {t("ui.vies_held.title", { count: rows.length })}
        </span>
        <span className="tabular-nums text-[10px] text-amber-600/70 ml-2">{t("ui.vies_held.caption")}</span>
        {locked && (   /* A2-581 lead 12: the server refuses decisions on a signed or locked calc */
          <span data-testid="vies-held-locked" className="text-xs text-amber-800 dark:text-amber-200 ml-2">
            {lockedBy === "frozen"
              ? t("ui.vies_held.locked_frozen", { defaultValue: "Locked for filing: these can no longer be decided here. To change the filed return, supersede it on Step 6." })
              : lockedBy === "replaced"
                ? t("ui.vies_held.locked_replaced", { defaultValue: "A newer calculation has replaced this one: decide these on the current calculation." })
                : t("ui.vies_held.locked", { defaultValue: "Signed off: these can no longer be decided on this calculation. To change them, run the calculation again (Step 3)." })}
          </span>
        )}
        <button
          type="button"
          data-testid="vies-held-toggle"
          aria-expanded={open}
          aria-controls="vies-held-rows"
          onClick={() => setOpen((o) => !o)}
          className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-md border border-amber-600/60 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-500"
        >
          {open
            ? t("ui.vies_held.hide", { defaultValue: "Hide rows" })
            : t("ui.vies_held.show", { count: pending.length, defaultValue: `Decide ${pending.length} row(s) →` })}
        </button>
      </header>
      <div data-testid="vies-held-summary" className="px-4 pb-3 text-xs text-amber-800 dark:text-amber-200 tabular-nums">
        {Object.entries(netBy).map(([c, n]) => `${fmtMoney(n)} ${c}`).join(" + ")}
        {reasons.length > 0 && " · "}
        {reasons.map(([f, n]) => `${t(`flag_titles.${f}`, { defaultValue: f })} (${n})`).join(" · ")}
      </div>
      {open && (
      <div id="vies-held-rows">
      {pending.length > 0 && (
        <div data-testid="vies-held-bulk" className="px-4 py-3 border-t border-amber-700/40 flex flex-wrap items-start gap-3">
          <label className="flex items-center gap-1.5 text-xs text-amber-800 dark:text-amber-200 pt-1.5">
            <input
              type="checkbox"
              data-testid="vies-held-select-all"
              checked={chosen.length === pending.length}
              onChange={(e) => setPicked(e.target.checked ? new Set(pending.map((r) => r.txn_id)) : new Set())}
            />
            {t("ui.vies_held.select_all", { count: pending.length, defaultValue: `Select all ${pending.length}` })}
          </label>
          <div className="w-72">
            <ReasonPicker
              testid="vies-reason-bulk"
              value={bulkReason}
              onChange={setBulkReason}
              presets={VIES_PRESETS}
              minChars={VIES_MIN_REASON_LEN}
              placeholder={t("ui.vies_held.reason_placeholder")}
            />
          </div>
          <div className="flex flex-wrap gap-1 pt-1">
            {shared.map((b) => {
              const v = computeValidity(bulkReason, VIES_MIN_REASON_LEN);
              const label = b === "B2B_ICS" && chosen[0]?.confirms_as && chosen[0].confirms_as !== "B2B_ICS"
                ? labelFor("bucket", chosen[0].confirms_as) : labelFor("bucket", b);
              return (
                <button
                  key={b}
                  type="button"
                  data-testid={`vies-bulk-${b}`}
                  disabled={locked || !v.lengthOk || !v.placeholdersOk || !!progress}
                  onClick={() => applyBulk(b)}
                  className="bg-amber-700/60 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 text-xs rounded"
                >
                  {t("ui.vies_held.apply_to", { count: chosen.length, label, defaultValue: `${label} — apply to ${chosen.length}` })}
                </button>
              );
            })}
            {progress && (
              <span data-testid="vies-bulk-progress" aria-live="polite" className="text-xs tabular-nums text-amber-800 dark:text-amber-200">
                {t("ui.vies_held.applying", { done: progress.done, total: progress.total, defaultValue: `Saving ${progress.done} of ${progress.total}…` })}
              </span>
            )}
          </div>
        </div>
      )}
      <table className="w-full tabular-nums text-xs">
        <thead className="bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-[10px] tracking-normal">
          <tr>
            <th className="px-3 py-2 w-6"><span className="sr-only">{t("ui.vies_held.col_pick", { defaultValue: "Select" })}</span></th>
            <th className="px-3 py-2 text-left">{t("ui.vies_held.col_order")}</th>
            <th className="px-3 py-2 text-left">{t("ui.vies_held.col_route")}</th>
            <th className="px-3 py-2 text-left">{t("ui.vies_held.col_vat")}</th>
            <th className="px-3 py-2 text-right">{t("ui.vies_held.col_net")}</th>
            <th className="px-3 py-2 text-left">{t("ui.vies_held.col_flags")}</th>
            <th className="px-3 py-2 text-left">{t("ui.vies_held.col_action")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.txn_id} data-testid={`vies-held-row-${r.order_id}`}
                className="border-t border-amber-900/40 align-top">
              <td className="px-3 py-2">
                {!r.override && (
                  <input type="checkbox" data-testid={`vies-pick-${r.order_id}`}
                    aria-label={r.order_id}
                    checked={picked.has(r.txn_id)} onChange={() => toggle(r.txn_id)} />
                )}
              </td>
              <td className="px-3 py-2 text-slate-800 dark:text-slate-100">{r.order_id}</td>
              <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{r.seller_country}→{r.buyer_country}</td>
              <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{r.buyer_vat_number || "—"}</td>
              <td className="px-3 py-2 text-right text-slate-900 dark:text-slate-50">{fmtMoney(r.net_amount)} {r.currency}</td>
              <td className="px-3 py-2 text-amber-700 dark:text-amber-300">{(r.review_flags || []).map((f) => t(`flag_titles.${f}`, { defaultValue: f })).join(" · ")}</td>
              <td className="px-3 py-2">
                {r.override ? (
                  <span className="text-emerald-700 dark:text-emerald-300 text-[11px]">
                    {t("ui.vies_held.overridden_to", { bucket: labelFor("bucket", r.override.decision) })} · {r.override.reason}
                  </span>
                ) : (
                  <div className="flex flex-col gap-1 w-64">
                    {/* v0.5.0c — preset dropdown + textarea via
                        ReasonPicker. Replaces the bare `<input>` so
                        operators no longer face a blank field; placeholder
                        tokens fail loud per the round-C sign-off. */}
                    <ReasonPicker
                      testid={`vies-reason-${r.order_id}`}
                      value={draft[r.txn_id]?.reason || ""}
                      onChange={(v) => setDraft((d) => ({ ...d, [r.txn_id]: { reason: v }}))}
                      presets={VIES_PRESETS}
                      minChars={VIES_MIN_REASON_LEN}
                      placeholder={t("ui.vies_held.reason_placeholder")}
                      // v0.6 — "direct-confirm" carries <DATE>, and
                      // "vies-outage" carries <COUNTRY>+<DATE>, both
                      // unfilled — same blocking-placeholder bug as
                      // elsewhere. <COUNTRY> is only substituted when
                      // actually known; otherwise it's left for the
                      // fail-loud validator to catch, same discipline
                      // as ReasonPicker's own tokenAutofill contract.
                      onPresetPicked={(p) => {
                        // A2-430 (operator 2026-10-19): both dates name a
                        // past event (the customer's confirmation, the
                        // outage) — picked from a calendar, never today.
                        if (p.id === "vies-outage" && r.buyer_country) {
                          const filled = (p.template || "").replace("<COUNTRY>", r.buyer_country);
                          setDraft((d) => ({ ...d, [r.txn_id]: { reason: filled } }));
                        }
                      }}
                    />
                    <div className="flex gap-1">
                      {/* A2-493 — only the decisions valid for this seller (backend: targets) */}
                      {(r.targets || ["B2B_ICS", "LOCAL_SALE", "DISTANCE_SALE"]).map((b) => {
                        const v = computeValidity(
                          draft[r.txn_id]?.reason || "",
                          VIES_MIN_REASON_LEN,
                        );
                        const disabled = locked || !v.lengthOk || !v.placeholdersOk;   // A2-581 lead 12
                        return (
                          <button
                            key={b}
                            data-testid={`vies-override-${r.order_id}-${b}`}
                            data-resolution-class="self-resolve"
                            data-resolution-endpoint="POST /api/runs/calc/{calc_id}/vies-override"
                            disabled={disabled}
                            onClick={() => apply(r.txn_id, b)}
                            className="bg-amber-700/60 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-0.5 text-[10px] tabular-nums rounded"
                          >{b === "B2B_ICS" && r.confirms_as && r.confirms_as !== "B2B_ICS"
                              ? labelFor("bucket", r.confirms_as) : b}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      )}
    </section>
  );
};
